import { Icon } from "@iconify/react";
import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AppointmentForm.css";
import { BASE_URL } from "../../helpers/api";
import { useNavigate } from "react-router-dom";
import LoginPopup from "../LoginPopup/LoginPopup";

export default function AppointmentForm() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");;
  const [reason, setReason] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [slotIndex, setSlotIndex] = useState(0);
  // The specific schedule row the selected slot's serial_no is native to —
  // usually the same as timeSlots.scheduleId, but a day can be covered by
  // more than one schedule row (e.g. a general block plus an extra-hours
  // block), and serial_no only means anything paired with the schedule row
  // it was computed against.
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [location, setLocation] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [doctorList, setDoctorList] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [timeSlots, setTimeSlots] = useState({ slots: [], scheduleId: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [locationsData, setLocationsData] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [unavailableRanges, setUnavailableRanges] = useState([]);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  // Set when the patient arrived here already having picked a doctor whose
  // department+location has exactly one valid combination (see
  // helpers/doctorPrefill.js) — nothing left to choose, so those two fields
  // are pre-filled and disabled instead of asking the patient to re-pick
  // something with only one right answer.
  const [doctorLocked, setDoctorLocked] = useState(false);
  // A Department pre-filled from a doctor's profile (see
  // helpers/doctorPrefill.js) but with no matching Location saved alongside
  // it — Location isn't a doctor property, so there's nothing to restore it
  // from. Applied the first time the patient picks any location, then
  // cleared, rather than requiring a location to already be set.
  const pendingSpecialityRef = useRef(null);
  // A doctor pre-filled from their profile page, shown by name immediately
  // even before a Location is picked — the real doctorList (scoped by
  // Location+Department) is empty until then, so without this the select
  // would just show blank despite selectedDoctor already holding the right
  // id. Cleared the moment the real, location-scoped list takes over.
  const [doctorPreview, setDoctorPreview] = useState(null);
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);



  // Dynamic timezone map built from API response
const timezoneMap = React.useMemo(() => {
  const map = {};
  locationsData.forEach(loc => {
    map[String(loc.location_id)] = loc.timezone_label || "UTC";
  });
  return map;
}, [locationsData]);

  // Friendly label for the raw IANA zone so patients don't need to decode
  // "America/Chicago" themselves — falls back to the raw zone name for any
  // location not explicitly mapped.
  const TIMEZONE_LABELS = {
    "America/Chicago": "US Time",
    "Asia/Kolkata": "India Time (IST)",
  };
  const selectedTimezone = timezoneMap[String(location)];
  const timezoneLabel = selectedTimezone
    ? (TIMEZONE_LABELS[selectedTimezone] || selectedTimezone)
    : null;

  // Restore a doctor pre-selected elsewhere (e.g. from a doctor's profile
  // "Book Appointment" button) — browsing/filling the form doesn't require
  // being signed in; that's only checked at actual submit time below.
  useEffect(() => {
    const storedDoctor = localStorage.getItem("selectedDoctor");
    if (storedDoctor) {
      const doctorObj = JSON.parse(storedDoctor);
      setSelectedDoctor(doctorObj.id);
    }
  }, []);


  // ── Define OUTSIDE useEffect so it's accessible everywhere ──
  const fetchProfile = async () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token) return;
  try {
    const res  = await fetch(`${BASE_URL}/user/profile?token=${token}`);
    const data = await res.json();
    if (data && !data.error) {
      const fullName = [data.firstname, data.lastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      setName(fullName);
      setEmail(data.email  || "");
      setPhone(data.mobile || "");

     // ✅ CORRECT
    const storage = localStorage.getItem("token") ? localStorage : sessionStorage;
    storage.setItem("userName",  fullName);
    storage.setItem("userEmail", data.email  || "");
    storage.setItem("userPhone", data.mobile || "");
    }
  } catch (err) {
    console.error("Error fetching profile:", err);
  }
  };

    // ── Call on page load ──
    useEffect(() => {
      fetchProfile();
    }, []);

  // Fetch locations data on page load
  useEffect(() => {
    const fetchLocationsData = async () => {
      try {
        const res  = await fetch(`${BASE_URL}/location-specialties-doctors`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setLocationsData(data);

          const savedLocation   = localStorage.getItem("selectedLocation");
          const savedSpeciality = localStorage.getItem("selectedSpeciality");
          const savedDate       = localStorage.getItem("selectedDate");
          const savedDoctorId   = localStorage.getItem("selectedDoctorId");

          if (savedLocation)  setLocation(savedLocation);
          if (savedDate)      setSelectedDate(new Date(savedDate));

          if (savedLocation && savedSpeciality) {
            const locData = data.find(
              (loc) => String(loc.location_id) === String(savedLocation)
            );
            if (
              locData &&
              locData.specialties.some(
                (s) => String(s.id) === String(savedSpeciality)
              )
            ) {
              setSpeciality(savedSpeciality);
            }
          } else if (savedSpeciality) {
            // Department was pre-filled without a Location alongside it
            // (e.g. from a doctor's profile page) — hold onto it and apply
            // it the first time the patient picks any location.
            pendingSpecialityRef.current = savedSpeciality;
          }

          if (savedDoctorId) {
            setSelectedDoctor(Number(savedDoctorId));
            const savedDoctorObj = localStorage.getItem("selectedDoctor");
            if (savedDoctorObj) {
              try {
                const parsed = JSON.parse(savedDoctorObj);
                if (parsed?.name) setDoctorPreview({ id: Number(savedDoctorId), name: parsed.name });
              } catch {}
            }
          }

          // Consume-once: this flag should only govern the visit it was set
          // for. Left in localStorage, it would wrongly disable Location/
          // Department on some later, unrelated visit to this form. Only
          // touch doctorLocked when the key is actually still present —
          // React 18 StrictMode runs this effect twice on mount in dev, and
          // the second pass must be a no-op here rather than reading the
          // now-missing key and clobbering the first pass's true back to false.
          const lockedFlag = localStorage.getItem("selectedDoctorLocked");
          if (lockedFlag !== null) {
            setDoctorLocked(lockedFlag === "true");
            localStorage.removeItem("selectedDoctorLocked");
          }
        }
      } catch (err) {
        console.error("Error fetching locations with doctors:", err);
      }
    };
    fetchLocationsData();
  }, []);

  // Update doctor list when location/speciality changes
  // ✅ FIX: Always reset selectedDoctor when location/speciality changes
  //         so slots are always fetched fresh for the new doctor
  useEffect(() => {
    if (!location || !speciality || !locationsData.length) {
      setDoctorList([]);
      // Deliberately NOT resetting selectedDoctor here — a doctor pre-filled
      // from their profile page (see doctorPreview above) has no location-
      // scoped list to validate against yet, but the id is still correct and
      // must survive until the patient actually picks a Location.
      setTimeSlot("");
      setSlotIndex(0); setSelectedScheduleId(null); // ← add this
      setTimeSlots({ slots: [], scheduleId: null });
      return;
    }

    // The real, location-scoped list has taken over — the early preview no
    // longer applies (it'll be replaced by a real <option> below if the
    // doctor is actually available here, same reconciliation as before).
    setDoctorPreview(null);

    const locData = locationsData.find(
      (loc) => String(loc.location_id) === String(location)
    );
    if (!locData) { setDoctorList([]); return; }

    const specData = locData.specialties.find(
      (spec) => String(spec.id) === String(speciality)
    );
    if (!specData) { setDoctorList([]); return; }

    const doctors = (specData.doctors || []).map((doc) => ({
      doctor_id:   doc.doctor_id,
      doctor_name: doc.doctor_name,
    }));

    setDoctorList(doctors);

    // Keep whichever doctor is already selected if they're still valid for
    // this location+department (this is what makes a doctor pre-selected
    // from a profile page's "Book Appointment" survive this list rebuild —
    // otherwise it would silently get overwritten by the first doctor in
    // the list on every mount). Only fall back to the first doctor when
    // there's no previous selection, or it doesn't belong to this list.
    setSelectedDoctor((prevId) => {
      if (prevId && doctors.some((d) => String(d.doctor_id) === String(prevId))) {
        return prevId;
      }
      return doctors.length > 0 ? doctors[0].doctor_id : null;
    });

    // Reset slots whenever doctor list changes
    setTimeSlot("");
    setSlotIndex(0); setSelectedScheduleId(null); // ← add this
    setTimeSlots({ slots: [], scheduleId: null });

  }, [speciality, location, locationsData]);

  // ✅ FIX: Handle manual doctor change — always reset slots and force re-fetch
  const handleDoctorChange = (e) => {
    const doctorId = parseInt(e.target.value);
    setTimeSlot("");
    setSlotIndex(0); setSelectedScheduleId(null); // ← add this
    setTimeSlots({ slots: [], scheduleId: null });
    setSelectedDoctor(doctorId); // set AFTER reset so useEffect triggers with new value
    localStorage.setItem("selectedDoctorId", doctorId);
  };

  // Fetch which weekdays this doctor actually works at the selected location,
  // plus any admin-panel leave ranges (doctor_unavailability), so the calendar
  // greys out both days they never work and one-off time off.
  useEffect(() => {
    if (!selectedDoctor || !location) {
      setAvailableDays([]);
      setUnavailableRanges([]);
      return;
    }
    fetch(`${BASE_URL}/doctors/${selectedDoctor}/available-days?location_id=${location}&specialty_id=${speciality || ""}`)
      .then((res) => res.json())
      .then((data) => {
        setAvailableDays(Array.isArray(data.available_days) ? data.available_days : []);
        setUnavailableRanges(
          Array.isArray(data.unavailable_ranges) ? data.unavailable_ranges : []
        );
      })
      .catch((err) => {
        console.error("Error fetching available days:", err);
        setAvailableDays([]);
        setUnavailableRanges([]);
      });
  }, [selectedDoctor, location, speciality]);

  const WEEKDAY_NAMES = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
  ];
  const toDateStr = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const isOnLeave = (date) => {
    const dateStr = toDateStr(date);
    return unavailableRanges.some(
      (r) => dateStr >= r.start_date && dateStr <= r.end_date
    );
  };
  const isDateAvailable = (date) => {
    if (!selectedDoctor) return true;
    if (isOnLeave(date)) return false;
    return availableDays.includes(WEEKDAY_NAMES[date.getDay()]);
  };

  // Fetch available time slots
  // ✅ FIX: Runs whenever selectedDoctor, selectedDate, or location changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDoctor || !selectedDate || !location) {
        setTimeSlots({ slots: [], scheduleId: null });
        return;
      }

      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

      try {
        const response = await fetch(
          `${BASE_URL}/doctors/${selectedDoctor}/availability`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: dateStr, location_id: location, specialty_id: speciality || null }),
          }
        );

        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          const matchedDay = data.find((item) => item.date === dateStr);
          setTimeSlots(
            matchedDay?.time_slots
              ? { slots: matchedDay.time_slots, scheduleId: matchedDay.schedule_id }
              : { slots: [], scheduleId: null }
          );
        } else {
          setTimeSlots({ slots: [], scheduleId: null });
        }
      } catch (err) {
        console.error("Error fetching slots:", err);
        setTimeSlots({ slots: [], scheduleId: null });
      }
    };

    fetchSlots();
  }, [selectedDoctor, selectedDate, location, speciality]);

  // Form validation
  const validateEmail = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!value) return "Email is required";
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validateForm = () => {
    const errors = {};
    // name, phone, email are readonly — no validation needed
    if (!location)       errors.location   = "Location is required";
    if (!speciality)     errors.speciality = "Department is required";
    if (!selectedDoctor) errors.doctor     = "Doctor is required";
    if (!selectedDate)   errors.date       = "Date is required";
    if (!timeSlot)       errors.timeSlot   = "Time slot is required";
    if (!reason)         errors.reason     = "Reason for visit is required";
    return errors;
  };

  // const serialNo = Array.isArray(timeSlots.slots)
  //   ? timeSlots.slots.indexOf(timeSlot) + 1
  //   : null;

  const serialNo = slotIndex;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    const token  = localStorage.getItem("token")  || sessionStorage.getItem("token");
    if (!userId || !token) {
      localStorage.setItem("redirectAfterLogin", window.location.pathname);
      setShowLoginPopup(true);
      return;
    }

    setSubmitted(true);
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      name,
      email,
      mobile: phone,
      location,
      token: localStorage.getItem("token") || sessionStorage.getItem("token") || null,
      specialty_id: speciality,
      doctor_id:    selectedDoctor,
      date: selectedDate
        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
        : null,
      time_slot:   timeSlot,
      problem:     reason,
      serial_no:   serialNo,
      schedule_id: selectedScheduleId ?? timeSlots.scheduleId,
    };

    try {
      const res  = await fetch(`${BASE_URL}/appointments/submit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      console.log("DEBUG RESPONSE:", data);

      // ── Check if the request actually succeeded ──
      const isSuccess = res.ok && data.appointment_id;

      if (isSuccess) {
        // ✅ SUCCESS
        localStorage.setItem("appointment_id", data.appointment_id);
        setModalMessage(data.message || "Appointment successfully created!");
        setIsSuccess(true);
        setModalVisible(true);

        // Reset form only on success
        setReason("");
        setTimeSlot(""); setSlotIndex(0); setSelectedScheduleId(null); setSpeciality(""); setLocation("");
        setSelectedDoctor(null); setDoctorList([]);
        setSelectedDate(null);
        setTimeSlots({ slots: [], scheduleId: null });
        setSubmitted(false); setFormErrors({});

        fetchProfile();

        localStorage.removeItem("selectedSpeciality");
        localStorage.removeItem("selectedLocation");
        localStorage.removeItem("selectedDate");
        localStorage.removeItem("selectedDoctorId");
      } else {
        // ❌ API ERROR (slot booked, invalid session, etc.)
        const errorMsg = data.error 
                      || data.message 
                      || "Something went wrong. Please try again.";
        setModalMessage(errorMsg);
        setIsSuccess(false);
        setModalVisible(true);
      }
    } catch (err) {
      // ❌ NETWORK ERROR (no internet, server down, CORS, etc.)
      console.error("Error submitting appointment:", err);
      setModalMessage("Unable to connect to the server. Please check your internet and try again.");
      setIsSuccess(false);
      setModalVisible(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="row">

        {/* Name */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Name <span style={{color: "red"}}>*</span></label>
          <input
              type="text"
              className="cs_form_field"
              value={name}
              readOnly
              style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.name && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.name}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Phone */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Phone Number <span style={{color: "red"}}>*</span></label>
          <input
              type="text"
              className="cs_form_field"
              value={phone}
              readOnly
              style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.phone && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.phone}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Email */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Mail ID <span style={{color: "red"}}>*</span></label>
          <input
            type="text"
            className="cs_form_field"
            value={email}
            readOnly
            style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.email && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.email}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Location */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Location <span style={{color: "red"}}>*</span></label>
          <select
            value={location}
            onChange={(e) => {
              const newLocation = e.target.value;
              setLocation(newLocation);
              localStorage.setItem("selectedLocation", newLocation);

              // Apply a department pre-filled from a doctor's profile the
              // first time a location is picked, since it wasn't tied to any
              // particular location — otherwise fall back to the normal
              // reset-on-location-change behavior.
              const pending = pendingSpecialityRef.current;
              const locData = locationsData.find((loc) => String(loc.location_id) === String(newLocation));
              if (pending && locData?.specialties.some((s) => String(s.id) === String(pending))) {
                setSpeciality(pending);
                localStorage.setItem("selectedSpeciality", pending);
              } else {
                setSpeciality("");
              }
              pendingSpecialityRef.current = null;

              // Not resetting selectedDoctor here — the doctor-list-rebuild
              // effect below already reconciles it against the new location+
              // department's real list (keeps it if still valid there, else
              // falls back to the first doctor), which is what actually
              // preserves a doctor pre-filled from their profile page.
              // Nulling it here first would beat that check and always fall
              // through to "first doctor in the list" instead.
              setDoctorList([]);
              setTimeSlot("");
              setSlotIndex(0); setSelectedScheduleId(null); // ← add this
              setTimeSlots({ slots: [], scheduleId: null });
            }}
            className="cs_form_field"
          >
            <option value="">Select Location</option>
            {locationsData.map((loc) => (
              <option key={loc.location_id} value={loc.location_id}>
                {loc.location_name}
              </option>
            ))}
          </select>
          {submitted && formErrors.location && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.location}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Department */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Department <span style={{color: "red"}}>*</span></label>
          <select
            value={speciality}
            onChange={(e) => {
              setSpeciality(e.target.value);
              localStorage.setItem("selectedSpeciality", e.target.value);
              setSelectedDoctor(null);
              setTimeSlot("");
              setSlotIndex(0); setSelectedScheduleId(null); // ← add this
              setTimeSlots({ slots: [], scheduleId: null });
            }}
            className="cs_form_field"
            disabled={!location || doctorLocked}
          >
            <option value="">Select Department</option>
            {locationsData
              .find((loc) => String(loc.location_id) === String(location))
              ?.specialties.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name}
                </option>
              ))}
          </select>
          {doctorLocked && (
            <p style={{ fontSize: "0.8125rem", color: "#6b7280", marginTop: "4px", marginBottom: 0 }}>
              This doctor is only available in this department.
            </p>
          )}
          {submitted && formErrors.speciality && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.speciality}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Doctor */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Doctor Name <span style={{color: "red"}}>*</span></label>
          <select
            className="cs_form_field"
            value={selectedDoctor || ""}
            onChange={handleDoctorChange}
          >
            <option value="">Select Doctor</option>
            {doctorList.length === 0 && doctorPreview && String(doctorPreview.id) === String(selectedDoctor) ? (
              <option value={doctorPreview.id}>{doctorPreview.name}</option>
            ) : (
              doctorList.map((doc) => (
                <option key={doc.doctor_id} value={doc.doctor_id}>
                  {doc.doctor_name}
                </option>
              ))
            )}
          </select>
          {submitted && formErrors.doctor && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.doctor}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Date */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Preferred Date <span style={{color: "red"}}>*</span></label>
          <div className="cs_with_icon_input relative">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setTimeSlot("");
                setSlotIndex(0); setSelectedScheduleId(null); // ← add this
                setTimeSlots({ slots: [], scheduleId: null });
                if (date)
                  localStorage.setItem("selectedDate", date.toISOString().split("T")[0]);
              }}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              filterDate={isDateAvailable}
              isClearable
              placeholderText="dd/mm/yyyy"
              className="cs_form_field w-full"
            />
            <i className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Icon icon="fa6-solid:calendar-days" />
            </i>
          </div>
          {submitted && formErrors.date && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.date}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Time Slots */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">
            Preferred Time <span style={{color: "red"}}>*</span>
            {timezoneLabel && (
              <span className="cs_slot_timezone_badge">{timezoneLabel}</span>
            )}
          </label>
          {(() => {
            const availableSlots = (Array.isArray(timeSlots.slots) ? timeSlots.slots : [])
              .filter((slot) => !slot.booked)
              .map((slot) => {
                const [hourStr, minuteStr] = slot.time.split(":");
                const hour        = parseInt(hourStr);
                const period      = hour >= 12 ? "PM" : "AM";
                const displayHour = String(hour % 12 || 12).padStart(2, "0");
                return { ...slot, displayTime: `${displayHour}:${minuteStr} ${period}` };
              });

            return availableSlots.length > 0 ? (
              <select
                className="cs_form_field w-full"
                value={timeSlot}
                onChange={(e) => {
                  const found = availableSlots.find((s) => s.displayTime === e.target.value);
                  setTimeSlot(e.target.value);
                  setSlotIndex(found ? found.serial_no : 0);
                  setSelectedScheduleId(found ? (found.schedule_id ?? timeSlots.scheduleId) : null);
                }}
              >
                <option value="">Select a time</option>
                {availableSlots.map((slot, idx) => (
                  <option key={idx} value={slot.displayTime}>
                    {slot.displayTime}
                  </option>
                ))}
              </select>
            ) : (
              <p className="cs_slot_empty">No time slots available</p>
            );
          })()}
          {submitted && formErrors.timeSlot && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.timeSlot}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Reason */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Reason for Visit <span style={{color: "red"}}>*</span></label>
          <div className="cs_radio_group">
            {["Routine Checkup", "New Patient Visit", "Specific Concern"].map((r) => (
              <div className="cs_radio_wrap" key={r}>
                <input
                  className="cs_radio_input"
                  type="radio"
                  name="reasonForVisit"
                  id={r}
                  value={r}
                  checked={reason === r}
                  onChange={(e) => setReason(e.target.value)}
                />
                <label className="cs_radio_label" htmlFor={r}>{r}</label>
              </div>
            ))}
          </div>
          {submitted && formErrors.reason && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.reason}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Submit */}
        <div className="col-lg-12">
          <button type="submit" className="cs_btn cs_style_1">
            <span>Submit</span>
            <i>
              <img src="/images/icons/arrow_white.svg" alt="Icon" />
              <img src="/images/icons/arrow_white.svg" alt="Icon" />
            </i>
          </button>
        </div>

      </form>

      {/* Success / Error Modal */}
      {modalVisible && (
        <div className="modal-container">
          <button
            onClick={() => setModalVisible(false)}
            style={{
              position: "absolute", top: "10px", right: "10px",
              background: "transparent", border: "none",
              fontSize: "22px", cursor: "pointer", color: "gray", fontWeight: "normal",
            }}
          >
            ×
          </button>

          {/* Icon based on result */}
          {/* <div style={{ textAlign: "center", marginBottom: "10px" }}>
            {isSuccess ? (
              <Icon icon="fa6-solid:circle-check" style={{ fontSize: "48px", color: "#28a745" }} />
            ) : (
              <Icon icon="fa6-solid:circle-exclamation" style={{ fontSize: "48px", color: "#dc3545" }} />
            )}
          </div> */}

          {/* Heading */}
          <h3 style={{ textAlign: "center", marginBottom: "10px"}}>
            {isSuccess ? "Appointment Booked" : "Booking Failed"}
          </h3>

          {/* Message */}
          <p style={{ textAlign: "center" }}>{modalMessage}</p>

          {/* Action button — different for success vs error */}
          {isSuccess ? (
            <button
              className="cs_btn cs_style_1"
              onClick={() => {
                setModalVisible(false);
                window.location.href = "/payment";
              }}
            >
              <span>Proceed to Payment</span>
              <i>
                <img src="/images/icons/arrow_white.svg" alt="Icon" />
                <img src="/images/icons/arrow_white.svg" alt="Icon" />
              </i>
            </button>
          ) : (
            <button
              className="cs_btn cs_style_1"
              onClick={() => setModalVisible(false)}
            >
              <span>Try Again</span>
            </button>
          )}
        </div>
      )}

      {/* Login Popup */}
      <LoginPopup show={showLoginPopup} redirectPath="/appointments" />
    </>
  );
}